import { useState, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for checking capacity availability
 * Provides debounced availability checking and state management
 */
export function useCapacityCheck() {
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'available' | 'warning' | 'full' | 'error'
  const [availableSlots, setAvailableSlots] = useState(null);
  const [totalSlots, setTotalSlots] = useState(null);
  const [message, setMessage] = useState('');
  const [nextAvailableWeeks, setNextAvailableWeeks] = useState([]);
  const [userCanOverride, setUserCanOverride] = useState(false);
  const [marketName, setMarketName] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const debounceRef = useRef(null);
  
  /**
   * Check availability for a specific market and date
   * @param {string} marketId - Market ID
   * @param {string} date - Selected date (YYYY-MM-DD or datetime string)
   * @param {number} debounceMs - Debounce delay in ms (default 500)
   */
  const checkAvailability = useCallback((marketId, date, debounceMs = 500) => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Validate inputs
    if (!marketId || !date) {
      setStatus('idle');
      return;
    }
    
    setStatus('loading');
    setIsLoading(true);
    
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          setError('Not authenticated');
          setStatus('error');
          return;
        }
        
        // Normalize date to YYYY-MM-DD
        const dateStr = date.split('T')[0];
        
        const response = await fetch(
          `${API_URL}/api/ops/capacity/availability?market_id=${marketId}&week_start_date=${dateStr}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to check availability');
        }
        
        const data = await response.json();
        
        // Update state
        setAvailableSlots(data.available_slots);
        setTotalSlots(data.total_slots);
        setMessage(data.message);
        setNextAvailableWeeks(data.next_available_weeks || []);
        setUserCanOverride(data.user_can_override);
        setMarketName(data.market_name);
        setWeekStartDate(data.week_start_date);
        setError(null);
        
        // Determine status based on slots
        if (data.available_slots === 0) {
          setStatus('full');
        } else if (data.available_slots <= 4) {
          setStatus('warning');
        } else {
          setStatus('available');
        }
        
      } catch (err) {
        console.error('Capacity check error:', err);
        setError(err.message);
        setStatus('error');
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);
  }, []);
  
  /**
   * Clear the capacity check state
   */
  const clearState = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setStatus('idle');
    setAvailableSlots(null);
    setTotalSlots(null);
    setMessage('');
    setNextAvailableWeeks([]);
    setUserCanOverride(false);
    setMarketName('');
    setWeekStartDate('');
    setIsLoading(false);
    setError(null);
  }, []);
  
  return {
    status,
    availableSlots,
    totalSlots,
    message,
    nextAvailableWeeks,
    userCanOverride,
    marketName,
    weekStartDate,
    isLoading,
    error,
    checkAvailability,
    clearState
  };
}

export default useCapacityCheck;
